import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Spinner from "../components/ui/Spinner";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";

function registerErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === "object") {
    const d = err.response.data as { message?: string };
    if (typeof d.message === "string" && d.message.trim()) {
      return d.message;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "Registration failed. Please try again.";
}

function passwordMeetsPolicy(p: string): boolean {
  return p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p);
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    businessName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.businessName.trim() ||
      !formData.email.trim() ||
      !formData.password
    ) {
      setError("All fields are required.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!passwordMeetsPolicy(formData.password)) {
      setError(
        "Password must be at least 8 characters with at least one uppercase letter, one lowercase letter, and one number."
      );
      return;
    }

    setLoading(true);
    try {
      await register({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        businessName: formData.businessName.trim(),
      });
      navigate("/billing", { replace: true });
    } catch (err: unknown) {
      setError(registerErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-emerald-500/60 placeholder:text-slate-500 focus:ring-2";

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900/85 p-8 shadow-2xl shadow-emerald-900/10">
        <div className="mb-8 text-center">
          <img src={logo} alt="IntelliWarrior" className="mx-auto mb-4 h-28 w-auto max-w-full object-contain" />
          <h1 className="text-xl font-semibold text-white">Create your operator account</h1>
          <p className="mt-1 text-sm text-slate-400">Start your 14-day free trial — no charge until day 15.</p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="mb-1 block text-sm text-slate-300">
                First name
              </label>
              <input
                id="firstName"
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="lastName" className="mb-1 block text-sm text-slate-300">
                Last name
              </label>
              <input
                id="lastName"
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                className={inputClass}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="businessName" className="mb-1 block text-sm text-slate-300">
              Business name
            </label>
            <input
              id="businessName"
              type="text"
              autoComplete="organization"
              placeholder="e.g., Fresh Start Credit Repair"
              value={formData.businessName}
              onChange={(e) => setFormData((p) => ({ ...p, businessName: e.target.value }))}
              className={inputClass}
              required
            />
            <p className="mt-1 text-xs text-slate-500">We&apos;ll use this to create your portal subdomain.</p>
          </div>

          <div>
            <label htmlFor="email" className="mb-1 block text-sm text-slate-300">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-sm text-slate-300">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
              className={inputClass}
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              Minimum 8 characters, one uppercase, one lowercase, and one number.
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="mb-1 block text-sm text-slate-300">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData((p) => ({ ...p, confirmPassword: e.target.value }))}
              className={inputClass}
              required
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/40 bg-red-900/20 px-3 py-2 text-sm text-red-300">{error}</div>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-900/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Spinner className="h-4 w-4 border-2 border-white/30 border-t-white" />
                Creating account…
              </>
            ) : (
              "Create account"
            )}
          </button>

          <p className="text-center text-xs text-slate-500">
            By creating an account, you agree to our{" "}
            <Link to="/terms" className="text-emerald-400 hover:text-emerald-300">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-emerald-400 hover:text-emerald-300">
              Privacy Policy
            </Link>
            .
          </p>

          <p className="text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-emerald-400 hover:text-emerald-300">
              Log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
